import { useState, useEffect } from 'react';
import supabase from '../supabaseClient';
import { SB_URL, SB_KEY } from '../services/supabase-api';
import { initCont } from '../constants/formConfig';
import { Inp, RG } from './ui/FormControls';
import { terminarPedidoConDescuento } from '../services/pedidos-terminar';

const hoyISO = () => new Date().toISOString().slice(0, 10);
const formatFecha = (iso) => iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("es-ES") : "—";

const TIPO_DESTINO_LABEL = { 1: "Tipo 1 (Japón)", 2: "Tipo 2 (China)", 3: "Tipo 3 (GCC)", 0: "Otro" };

const subirAlbaran = async (file, nj) => {
  try {
    const carpeta = nj.trim().toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9_-]/g, "_");
    const ext = file.name.split(".").pop();
    const nombre = `internacional/${carpeta}/albaran_${Date.now()}.${ext}`;
    const r = await fetch(`${SB_URL}/storage/v1/object/Documentos-registros/${nombre}`, {
      method: "POST",
      headers: { "apikey": SB_KEY, "Authorization": `Bearer ${SB_KEY}`, "Content-Type": file.type },
      body: file
    });
    if (!r.ok) return null;
    return `${SB_URL}/storage/v1/object/Documentos-registros/${nombre}`;
  } catch { return null; }
};

// ── OPERARIO (INTERNACIONAL): SALIDAS CONTENEDORES ────────────────
export const SalidasSection = ({ currentUser, guardar }) => {
  const almacen_id = currentUser?.almacen_id;

  const [nj, setNj] = useState("");
  const [fecha, setFecha] = useState(hoyISO());
  const [contenedor, setContenedor] = useState("");
  const [precinto, setPrecinto] = useState("");
  const [destino, setDestino] = useState("");
  const [tipoDestino, setTipoDestino] = useState("0");
  const [manta, setManta] = useState(false);
  const [bolsas, setBolsas] = useState(false);
  const [manipulado, setManipulado] = useState(false);
  const [archivo, setArchivo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [checklist, setChecklist] = useState(initCont().items);
  const [observaciones, setObservaciones] = useState("");

  const [pedidoEnlazado, setPedidoEnlazado] = useState(null);
  const [pedidosEnProceso, setPedidosEnProceso] = useState([]);
  const [pedidoSeleccionadoId, setPedidoSeleccionadoId] = useState("");
  const [marcarTerminado, setMarcarTerminado] = useState(true);

  const [confirmando, setConfirmando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!almacen_id) return;
    supabase.from('pedidos').select('*').eq('almacen_id', almacen_id).eq('estado', 'en_proceso')
      .then(({ data }) => setPedidosEnProceso(data || []))
      .catch(() => {});
  }, [almacen_id]);

  useEffect(() => {
    if (!nj.trim() || !almacen_id) { setPedidoEnlazado(null); return; }
    const timer = setTimeout(() => {
      supabase.from('pedidos').select('*').eq('almacen_id', almacen_id).eq('nj', nj.trim()).maybeSingle()
        .then(({ data }) => setPedidoEnlazado(data || null))
        .catch(() => setPedidoEnlazado(null));
    }, 400);
    return () => clearTimeout(timer);
  }, [nj, almacen_id]);

  const pedidoParaEnlazar = pedidoEnlazado || pedidosEnProceso.find(p => p.id === pedidoSeleccionadoId) || null;

  const formValido = nj.trim().length > 0;

  const limpiarFormulario = () => {
    setNj(""); setFecha(hoyISO()); setContenedor(""); setPrecinto(""); setDestino("");
    setTipoDestino("0"); setManta(false); setBolsas(false); setManipulado(false);
    setArchivo(null); setPreview(null); setChecklist(initCont().items); setObservaciones("");
    setPedidoEnlazado(null); setPedidoSeleccionadoId(""); setMarcarTerminado(true);
  };

  const confirmarSalida = async () => {
    setGuardando(true);
    try {
      const njTrim = nj.trim();
      const albaran_url = archivo ? await subirAlbaran(archivo, njTrim) : null;

      await guardar("Salida Contenedor", {
        nj: njTrim,
        contenedor: contenedor.trim(),
        precinto: precinto.trim(),
        destino: destino.trim(),
        tipoDestino: Number(tipoDestino),
        manta,
        bolsas,
        manipulado,
        checklist,
        observaciones,
        fotoAlbaran: albaran_url,
      });

      if (pedidoParaEnlazar) {
        if (marcarTerminado) {
          const { data: lineasPedido } = await supabase.from('pedidos_lineas').select('*').eq('pedido_id', pedidoParaEnlazar.id);
          const resultado = await terminarPedidoConDescuento({
            pedido: pedidoParaEnlazar,
            lineas: lineasPedido || [],
            numero_albaran: njTrim,
            nj: !pedidoEnlazado ? njTrim : undefined,
            usuario: currentUser?.user,
            almacen_id,
          });
          if (!resultado.ok) {
            window.alert(`La salida se ha registrado correctamente, pero el pedido ${pedidoParaEnlazar.numero_pedido} NO se pudo marcar como terminado:\n\n${resultado.error}\n\nAjusta el inventario y termínalo manualmente desde Pedidos.`);
          }
        } else if (!pedidoEnlazado) {
          await supabase.from('pedidos').update({ nj: njTrim }).eq('id', pedidoParaEnlazar.id);
        }
      }

      setConfirmando(false);
      limpiarFormulario();
      supabase.from('pedidos').select('*').eq('almacen_id', almacen_id).eq('estado', 'en_proceso')
        .then(({ data }) => setPedidosEnProceso(data || []))
        .catch(() => {});
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow p-4 space-y-3">
        <h2 className="font-bold text-gray-800 border-b pb-2">📦 Salida Contenedor</h2>
        <Inp label="Nº de documento NJ *" value={nj} onChange={setNj} placeholder="NJ 54832453" />

        {pedidoEnlazado && (
          <p className="text-sm text-green-700 font-semibold bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            ✅ Enlazado con pedido {pedidoEnlazado.numero_pedido} — {pedidoEnlazado.cliente || "sin cliente"}
          </p>
        )}
        {!pedidoEnlazado && pedidosEnProceso.length > 0 && (
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase">¿Corresponde a un pedido?</label>
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={pedidoSeleccionadoId}
              onChange={e => setPedidoSeleccionadoId(e.target.value)}
            >
              <option value="">Ninguno</option>
              {pedidosEnProceso.map(p => (
                <option key={p.id} value={p.id}>{p.numero_pedido} — {p.cliente || "sin cliente"}</option>
              ))}
            </select>
          </div>
        )}
        {pedidoParaEnlazar && (
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-cyan-600" checked={marcarTerminado} onChange={e => setMarcarTerminado(e.target.checked)} />
            Marcar pedido como terminado
          </label>
        )}

        <Inp label="Fecha" type="date" value={fecha} onChange={setFecha} />
        <div className="grid grid-cols-2 gap-3">
          <Inp label="Nº contenedor" value={contenedor} onChange={setContenedor} placeholder="ONEU 6982339" />
          <Inp label="Precinto" value={precinto} onChange={setPrecinto} placeholder="Nº precinto" />
        </div>
        <Inp label="Destino / Cliente" value={destino} onChange={setDestino} placeholder="Nombre del destino o cliente" />

        <div>
          <label className="text-xs font-semibold text-gray-600 uppercase">Tipo de destino</label>
          <select
            className="w-full border rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={tipoDestino}
            onChange={e => setTipoDestino(e.target.value)}
          >
            <option value="1">Tipo 1 (Japón)</option>
            <option value="2">Tipo 2 (China)</option>
            <option value="3">Tipo 3 (GCC)</option>
            <option value="0">Otro</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-cyan-600" checked={manta} onChange={e => setManta(e.target.checked)} />
            Manta absorbente
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-cyan-600" checked={bolsas} onChange={e => setBolsas(e.target.checked)} />
            Bolsas desecantes
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-cyan-600" checked={manipulado} onChange={e => setManipulado(e.target.checked)} />
            Manipulado por operario
          </label>
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-600 uppercase">Foto del albarán de salida</label>
          <label className="mt-1 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-6 cursor-pointer hover:bg-gray-50 transition-all">
            {preview ? (
              <img src={preview} alt="preview" className="max-h-48 rounded-lg object-contain" />
            ) : (
              <>
                <span className="text-3xl mb-2">📷</span>
                <span className="text-sm text-gray-500 font-semibold">Toca para hacer foto o seleccionar archivo</span>
                <span className="text-xs text-gray-400 mt-1">JPG, PNG, PDF — máx. 10MB</span>
              </>
            )}
            <input
              type="file"
              accept="image/*,application/pdf"
              capture="environment"
              className="hidden"
              onChange={e => {
                const f = e.target.files[0];
                if (!f) return;
                setArchivo(f);
                setPreview(f.type.startsWith("image/") ? URL.createObjectURL(f) : null);
              }}
            />
          </label>
          {archivo && <p className="text-xs text-green-600 font-semibold mt-1">✅ {archivo.name}</p>}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-4 space-y-4">
        <h2 className="font-bold text-gray-800 border-b pb-2">✅ Check-list Contenedor</h2>
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-blue-50 px-3 py-2"><p className="text-xs font-bold text-blue-800 uppercase">🚢 Estado Salida</p></div>
          <div className="divide-y">
            {Object.keys(checklist).map(k => (
              <div key={k} className="flex items-center justify-between px-3 py-2 gap-2">
                <span className="text-sm text-gray-700 flex-1">{k}</span>
                <RG value={checklist[k]} onChange={v => setChecklist(c => ({ ...c, [k]: v }))} opts={["Bueno", "Regular", "Malo"]} />
              </div>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-600 uppercase">Observaciones</label>
          <textarea className="w-full border rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400" rows={3} value={observaciones} onChange={e => setObservaciones(e.target.value)} />
        </div>
      </div>

      <button
        onClick={() => setConfirmando(true)}
        disabled={!formValido}
        className="w-full bg-cyan-600 text-white font-bold py-3 rounded-xl hover:bg-cyan-700 disabled:opacity-40 transition-all"
      >
        Revisar y confirmar →
      </button>

      {confirmando && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-4 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="font-bold text-gray-800 border-b pb-2">✅ Confirmar salida</h2>
            <div className="space-y-1 text-sm text-gray-700">
              <p><span className="font-semibold">NJ:</span> {nj}</p>
              <p><span className="font-semibold">Fecha:</span> {formatFecha(fecha)}</p>
              <p><span className="font-semibold">Contenedor:</span> {contenedor || "—"} · <span className="font-semibold">Precinto:</span> {precinto || "—"}</p>
              <p><span className="font-semibold">Destino:</span> {destino || "—"} · <span className="font-semibold">Tipo:</span> {TIPO_DESTINO_LABEL[Number(tipoDestino)]}</p>
              <p><span className="font-semibold">Manta:</span> {manta ? "Sí" : "No"} · <span className="font-semibold">Bolsas:</span> {bolsas ? "Sí" : "No"} · <span className="font-semibold">Manipulado:</span> {manipulado ? "Sí" : "No"}</p>
              <p><span className="font-semibold">Pedido:</span> {pedidoParaEnlazar ? `${pedidoParaEnlazar.numero_pedido} — ${pedidoParaEnlazar.cliente || "sin cliente"}${marcarTerminado ? " (se marcará Terminado)" : ""}` : "Sin enlazar"}</p>
            </div>
            <div className="border rounded-lg p-3 space-y-1">
              <p className="text-xs font-bold text-gray-500 uppercase">Check-list</p>
              {Object.keys(checklist).map(k => (
                <p key={k} className="text-xs text-gray-600">{k}: <span className="font-semibold">{checklist[k] || "—"}</span></p>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setConfirmando(false)} disabled={guardando} className="flex-1 border border-gray-300 text-gray-600 font-bold py-3 rounded-xl hover:bg-gray-50 disabled:opacity-40 transition-all">← Volver</button>
              <button onClick={confirmarSalida} disabled={guardando} className="flex-1 bg-cyan-600 text-white font-bold py-3 rounded-xl hover:bg-cyan-700 disabled:opacity-40 transition-all">
                {guardando ? "⏳ Guardando..." : "Confirmar →"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
